import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Dog, User, Clock, X, Cake, Send } from "lucide-react";
import POSNegozio from "./POSNegozio";

const todayISO = () => new Date().toISOString().slice(0, 10);

const normPhone = (p) => String(p || "").replace(/\s+/g, "");

async function resolveLoyaltyCustomer(a) {
  const phone = (a.owner_phone || a.customer_phone || "").trim();
  const name = (a.owner_name || a.customer_name || "").trim();
  const q = phone || name;
  if (!q) return null;
  try {
    const r = await api.get("/customers", { params: { q, limit: 20, mode: "toelettatura" } });
    const list = Array.isArray(r.data) ? r.data : [];
    const np = normPhone(phone);
    let match = np ? list.find((c) => normPhone(c.phone) === np) : null;
    if (!match && name) {
      const ln = name.toLowerCase();
      match = list.find((c) => (c.name || "").trim().toLowerCase() === ln || (c.owner_name || "").trim().toLowerCase() === ln);
    }
    if (match) return match;
    const created = await api.post("/customers", {
      name: name || a.pet_name || "Cliente",
      owner_name: name || undefined,
      phone: phone || undefined,
      email: a.owner_email || undefined,
      pet_name: a.pet_name || undefined,
      pet_type: a.pet_type || undefined,
      pet_breed: a.pet_breed || undefined,
      pet_birthdate: a.pet_birth_date || undefined,
      birthday_discount_enabled: a.birthday_discount_enabled === true,
      mode: "toelettatura",
    });
    return created.data;
  } catch {
    return null;
  }
}

async function catalogForMode() {
  try {
    const r = await api.get("/products?mode=toelettatura");
    return Array.isArray(r.data) ? r.data : [];
  } catch {
    return [];
  }
}

export default function POSToelettatura() {
  const inner = useRef(null);
  const [appts, setAppts] = useState([]);
  const [linkedAppt, setLinkedAppt] = useState(null);
  const [birthdayStatus, setBirthdayStatus] = useState(null);
  const [birthdayApplied, setBirthdayApplied] = useState(false);

  const loadAppts = () => api.get(`/appointments?date=${todayISO()}&kind=toelettatura`).then((r) => setAppts(Array.isArray(r.data) ? r.data : [])).catch(() => setAppts([]));
  useEffect(() => { loadAppts(); }, []);

  const resetLinkState = () => {
    setLinkedAppt(null);
    setBirthdayApplied(false);
    setBirthdayStatus(null);
  };

  const linkAppointment = async (a) => {
    const services = Array.isArray(a.services) ? [...a.services] : [];
    if (services.length === 0 && a.service_name) services.push({ name: a.service_name, price: a.service_price || 0, product_id: a.product_id });
    const catalog = await catalogForMode();
    const pet = a.pet_name || a.owner_name || "Appuntamento";
    const matchProd = (s) => {
      const byId = s.product_id ? catalog.find((p) => p.id === s.product_id) : null;
      const byName = !byId && s.name ? catalog.find((p) => (p.name || "").trim().toLowerCase() === String(s.name).trim().toLowerCase()) : null;
      return byId || byName || null;
    };
    const typed = Number(inner.current?.getKeypad?.().value) || 0;
    let newLines;
    if (typed > 0) {
      const first = services.map(matchProd).find(Boolean);
      newLines = [{
        product_id: null,
        name: pet,
        qty: 1,
        price: typed,
        vat: first?.vat ?? 22,
        category_id: first?.category_id,
        appointment_id: a.id,
        quick: true,
      }];
    } else {
      if (services.length === 0) toast.info("L'appuntamento non ha servizi associati — aggiungi manualmente dalla griglia");
      newLines = services.map((s) => {
        const prod = matchProd(s);
        return {
          product_id: prod?.id || s.product_id || null,
          name: `${s.name} — ${pet}`.trim(),
          qty: 1,
          price: s.price ?? prod?.price ?? 0,
          vat: prod?.vat ?? 22,
          category_id: prod?.category_id,
          appointment_id: a.id,
        };
      });
    }
    inner.current?.setLines((prev) => {
      const keep = (Array.isArray(prev) ? prev : []).filter((l) => l.appointment_id !== a.id);
      return [...keep, ...newLines];
    });
    if (typed > 0) inner.current?.clearKeypad?.();
    setLinkedAppt(a);
    setBirthdayApplied(false);
    toast.success(typed > 0 ? `${pet}: ${formatEUR(typed)}` : `Appuntamento di ${pet} caricato`);

    const cust = await resolveLoyaltyCustomer(a);
    let monthDiscount = false;
    try {
      const r = await api.get(`/appointments/${a.id}/birthday-status`);
      setBirthdayStatus(r.data);
      if (!typed && r.data.is_birthday_month && r.data.enabled && r.data.discount_pct > 0) {
        inner.current?.setDiscountPct(r.data.discount_pct);
        setBirthdayApplied(true);
        monthDiscount = true;
        toast.success(`🎂 Compleanno di ${a.pet_name}! Sconto ${r.data.discount_pct}% applicato.`, { duration: 5000 });
      }
    } catch {
      setBirthdayStatus(null);
    }
    if (cust) {
      inner.current?.setCustomer(cust, { skipBirthday: typed > 0 || monthDiscount });
    }
  };

  const unlinkAppt = () => {
    const id = linkedAppt?.id;
    if (id) {
      inner.current?.setLines((prev) => (Array.isArray(prev) ? prev : []).filter((l) => l.appointment_id !== id));
    }
    inner.current?.setDiscountPct(0);
    inner.current?.setCustomer?.(null, { skipBirthday: true });
    resetLinkState();
    toast.info("Appuntamento scollegato");
  };

  const removeBirthdayDiscount = () => {
    inner.current?.setDiscountPct(0);
    setBirthdayApplied(false);
    toast.info("Sconto compleanno rimosso");
  };

  const applyBirthdayDiscount = () => {
    inner.current?.setDiscountPct(birthdayStatus.discount_pct);
    setBirthdayApplied(true);
    toast.success(`Sconto ${birthdayStatus.discount_pct}% applicato`);
  };

  const sendBirthdayNow = async () => {
    if (!linkedAppt) return;
    try {
      const r = await api.post(`/appointments/${linkedAppt.id}/send-birthday-message`, { channel: "auto", force: birthdayStatus?.already_sent_this_year });
      if (r.data.whatsapp_url) {
        window.open(r.data.whatsapp_url, "_blank");
        toast.success("Apertura WhatsApp — tocca 'Invia'");
      } else {
        toast.success(`Auguri inviati via ${r.data.provider}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    }
  };

  const onAfterClose = async () => {
    if (linkedAppt?.id) {
      try { await api.patch(`/appointments/${linkedAppt.id}/status?status=completed`); } catch { /* ignore */ }
    }
    resetLinkState();
    loadAppts();
  };

  const topStrip = (appts?.length ?? 0) > 0 ? (
    <div className="border-b border-velta-border bg-white px-4 py-2 overflow-x-auto pos-scroll flex items-center gap-3 shrink-0" data-testid="toel-appts-strip">
      <span className="text-[10px] font-mono uppercase tracking-widest text-velta-mute shrink-0">Oggi:</span>
      {(Array.isArray(appts) ? appts : []).map((a) => {
        const time = (a.start_at || "").slice(11, 16);
        const isDone = a.status === "completed";
        return (
          <button
            key={a.id}
            data-testid={`toel-appt-${a.id}`}
            onClick={() => linkAppointment(a)}
            disabled={isDone}
            className={`shrink-0 border rounded-md px-3 py-1.5 text-left transition ${
              linkedAppt?.id === a.id ? "border-velta-ink bg-velta-bg" : "border-velta-border hover:border-velta-ink"
            } ${isDone ? "opacity-40" : ""}`}
          >
            <div className="flex items-center gap-1.5 text-[11px] font-mono text-velta-mute">
              <Clock className="w-3 h-3" /> {time}
              {isDone && <span className="text-[9px] uppercase font-bold text-velta-success ml-1">✓ Fatto</span>}
            </div>
            <div className="font-semibold text-sm leading-tight flex items-center gap-1">
              <Dog className="w-3.5 h-3.5" /> {a.pet_name || "—"} {a.pet_breed && <span className="text-[11px] font-normal text-velta-mute">({a.pet_breed})</span>}
            </div>
            <div className="text-[11px] text-velta-mute">{a.owner_name || a.customer_name || ""}</div>
          </button>
        );
      })}
    </div>
  ) : null;

  const cartHeaderExtra = linkedAppt ? (
    <div className="px-3 py-2 border-b border-velta-border text-[11px] bg-velta-bg/60 shrink-0" data-testid="toel-linked-appt">
      <div className="flex items-center justify-between">
        <div className="font-medium flex items-center gap-1"><Dog className="w-3 h-3" /> {linkedAppt.pet_name} {linkedAppt.pet_breed && `· ${linkedAppt.pet_breed}`}</div>
        <button onClick={unlinkAppt} className="text-velta-mute inline-flex items-center gap-1 hover:text-velta-error" data-testid="toel-unlink-appt">
          <X className="w-3 h-3" /> Scollega
        </button>
      </div>
      <div className="text-velta-mute flex items-center gap-1"><User className="w-3 h-3" /> {linkedAppt.owner_name || linkedAppt.customer_name}</div>

      {birthdayStatus?.is_birthday_month && birthdayStatus?.enabled && (
        <div className="mt-2 p-2 rounded bg-gradient-to-r from-pink-100 to-purple-100 border-2 border-[#EC4899]" data-testid="toel-birthday-banner">
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#DB2777]">
            <Cake className="w-3.5 h-3.5" /> 🎂 Compleanno di {linkedAppt.pet_name}!
          </div>
          <div className="text-[10px] text-velta-mute mt-0.5">
            {birthdayApplied ? `Sconto ${birthdayStatus.discount_pct}% applicato` : `Sconto ${birthdayStatus.discount_pct}% disponibile`}
          </div>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {birthdayApplied ? (
              <button data-testid="toel-birthday-remove" onClick={removeBirthdayDiscount} className="text-[10px] px-2 h-6 border border-[#EC4899] text-[#DB2777] rounded hover:bg-white">Rimuovi sconto</button>
            ) : (
              <button data-testid="toel-birthday-apply" onClick={applyBirthdayDiscount} className="text-[10px] px-2 h-6 bg-[#EC4899] text-white rounded hover:bg-[#DB2777]">Applica sconto</button>
            )}
            <button data-testid="toel-birthday-send-wa" onClick={sendBirthdayNow} className="text-[10px] px-2 h-6 bg-[#25D366] text-white rounded hover:bg-[#128C7E] inline-flex items-center gap-1">
              <Send className="w-2.5 h-2.5" /> Invia auguri
            </button>
          </div>
          {birthdayStatus.already_sent_this_year && (
            <div className="text-[9px] text-velta-success mt-1">✓ Auguri già inviati quest&apos;anno</div>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <POSNegozio
      mode="toelettatura"
      title="Velta One · Toelettatura"
      testidPrefix="toel"
      innerRef={inner}
      headerExtra={
        <Link to="/dashboard/appuntamenti" data-testid="toel-open-calendar" className="inline-flex items-center gap-2 h-9 px-3 bg-velta-ink text-white rounded-md text-xs font-medium hover:bg-[#162244]">
          <Calendar className="w-3.5 h-3.5" /> Calendario appuntamenti
        </Link>
      }
      topStrip={topStrip}
      cartHeaderExtra={cartHeaderExtra}
      onAfterClose={onAfterClose}
      onClearAll={resetLinkState}
    />
  );
}
